/* @jsx h */
import { defineComponent, h, PropType } from "vue";
import { RouterLink } from "vue-router";
import { useLocalization } from "@lemmonhq/uikit";

interface Restaurant {
  identifier: string;
  slug: string | null;
  name: { en: string | null };
  textTeaser: { en: string | null };
  image: { url: string };
  address: {
    streetAddress: string;
    addressLocality: string;
  };
  category: Record<string, any>;
  openingHours: string | string[];
  googleRating?: number | null;
  googleReviewCount?: number | null;
}

export const RestaurantCard = defineComponent({
  props: {
    restaurant: {
      type: Object as PropType<Restaurant>,
      required: true,
    },
    city: {
      type: String,
      default: "zurich",
    },
    newTab: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { t } = useLocalization();
    return () => {
      const imageUrl = props.restaurant.image?.url || null;
      const restaurantSlug =
        props.restaurant.slug || props.restaurant.identifier;
      const profileUrl = `/restaurant/${restaurantSlug}/reserve/tables`;
      // When opening in a new tab, let the browser handle navigation and pass
      // the security-hardening rel so the opened page can't reach `window.opener`.
      const linkTarget = props.newTab
        ? { target: "_blank", rel: "noopener noreferrer" }
        : {};
      const addressDisplay =
        [props.restaurant.address?.streetAddress].filter(Boolean).join(", ") ||
        t("customer.home.address_not_available");

      return (
        <article
          class="group rounded-2xl bg-white border border-gray-100 motion-safe:hover:-translate-y-1 hover:shadow-lg hover:border-gray-200 motion-safe:active:-translate-y-0.5 active:shadow-sm"
          style="transition: transform 300ms var(--ease-spring), box-shadow 300ms ease, border-color 200ms ease;"
        >
          {/* Image */}
          <RouterLink to={profileUrl} {...linkTarget}>
            <div class="aspect-[4/3] overflow-hidden cursor-pointer bg-gray-100 rounded-t-2xl card-img-overlay">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={
                    props.restaurant.name.en || t("customer.home.restaurant")
                  }
                  loading="lazy"
                  class="w-full h-full object-cover motion-safe:group-hover:scale-105 img-fade"
                  style="transition: transform 500ms var(--ease-spring), opacity 400ms ease;"
                  onLoad={(e: Event) => {
                    (e.target as HTMLImageElement).classList.add("loaded");
                  }}
                  onError={(e: Event) => {
                    const img = e.target as HTMLImageElement;
                    img.style.display = "none";
                    img.parentElement
                      ?.querySelector(".placeholder-initial")
                      ?.classList.remove("hidden");
                  }}
                />
              ) : null}
              {/* Placeholder -- shown when no image or image fails to load */}
              <div
                class={`w-full h-full flex items-center justify-center ${imageUrl ? "hidden placeholder-initial" : ""}`}
                style="background: linear-gradient(135deg, #f8f7f5 0%, #eceae6 100%);"
              >
                <span
                  class="text-5xl font-extralight text-gray-300/60 select-none"
                  style="letter-spacing: 0.05em; font-family: 'Montserrat', sans-serif;"
                  aria-hidden="true"
                >
                  {props.restaurant.name.en?.charAt(0)?.toUpperCase() || "?"}
                </span>
              </div>
            </div>
          </RouterLink>

          {/* Content */}
          <div class="space-y-3 px-4 md:px-5 pb-5 pt-4">
            {/* Title */}
            <RouterLink to={profileUrl} {...linkTarget}>
              <h3 class="text-xl font-medium text-gray-900 leading-tight cursor-pointer">
                {props.restaurant.name.en || t("customer.home.restaurant")}
              </h3>
            </RouterLink>

            {/* Location + Rating */}
            <div class="flex items-center justify-between">
              <div class="flex items-center text-gray-500">
                <span
                  class="material-symbols-outlined text-sm mr-2"
                  aria-hidden="true"
                >
                  location_on
                </span>
                <span class="text-sm font-light">{addressDisplay}</span>
              </div>
              {props.restaurant.googleRating &&
                props.restaurant.googleRating > 0 && (
                  <div
                    class="flex items-center gap-1 text-sm"
                    title={`${props.restaurant.googleReviewCount || 0} reviews`}
                  >
                    <span
                      class="material-symbols-outlined text-sm text-yellow-500"
                      aria-hidden="true"
                    >
                      star
                    </span>
                    <span class="font-medium text-gray-700">
                      {props.restaurant.googleRating.toFixed(1)}
                    </span>
                  </div>
                )}
            </div>

            {/* Actions */}
            <div class="pt-3 border-t border-gray-100 flex justify-center">
              <RouterLink
                to={profileUrl}
                {...linkTarget}
                class="inline-flex items-center min-h-[44px] text-sm font-medium"
                style="color: var(--accent);"
                aria-label={t("customer.home.view_details_for", [
                  props.restaurant.name.en || t("customer.home.restaurant"),
                ])}
              >
                {t("customer.home.view_details")}
                <span
                  class="material-symbols-outlined text-sm ml-1.5 motion-safe:group-hover:translate-x-1"
                  style="transition: transform 200ms var(--ease-spring);"
                  aria-hidden="true"
                >
                  arrow_forward
                </span>
              </RouterLink>
            </div>
          </div>
        </article>
      );
    };
  },
});
